/**
 * Isolate the context in an IIFE
 *
 * @param object $ jQuery object
 * @param object Drupal Drupal object
 * @param window window Current window object
 * @param document document Current document object
 */

(function ($, Drupal, window/* , document */) {
  const $window = $(window);

  // Tables are only wrapped in a scroll region below the `desktop` breakpoint
  // (992px) — above it they already fit without help, see `table { min-width:
  // ... }` in _global.scss.
  const MOBILE_QUERY = window.matchMedia('(max-width: 991.98px)');
  const scrollableTables = new Set();

  /**
   * Hides the edge fade on whichever side has nothing left to scroll to.
   *
   * @param object wrapper The `.table-wrapper-gradient` element.
   */
  function updateScrollFades (wrapper) {
    const content = wrapper.firstElementChild;
    const isAtStart = content.scrollLeft <= 0;
    const isAtEnd = content.scrollLeft + content.clientWidth >= content.scrollWidth - 1;

    wrapper.classList.toggle('hide-left-fade', isAtStart);
    wrapper.classList.toggle('hide-right-fade', isAtEnd);
  }

  /**
   * `<table>` becomes
   * `<div class="table-wrapper-gradient"><div class="table-wrapper-content">
   * <table></div></div>`: the inner div scrolls, the outer one positions the
   * edge fades (see the `.table-wrapper-*` rules in _global.scss).
   *
   * @param object table The table to wrap.
   */
  function wrapTable (table) {
    const wrapper = document.createElement('div');
    const content = document.createElement('div');

    wrapper.classList.add('table-wrapper-gradient');
    content.classList.add('table-wrapper-content');
    table.parentNode.insertBefore(wrapper, table);
    content.appendChild(table);
    wrapper.appendChild(content);

    // Bound to the scroll port, so it's discarded along with the wrapper on
    // unwrap rather than piling up across breakpoint changes.
    content.addEventListener('scroll', () => updateScrollFades(wrapper));
    updateScrollFades(wrapper);
  }

  /**
   * @param object table The table to lift back out of its wrapper.
   */
  function unwrapTable (table) {
    const wrapper = table.parentNode.parentNode;

    wrapper.parentNode.insertBefore(table, wrapper);
    wrapper.remove();
  }

  /**
   * @param object table The table to test.
   *
   * @return bool Whether the table currently sits inside a scroll wrapper.
   */
  function isWrapped (table) {
    return table.parentNode.classList.contains('table-wrapper-content');
  }

  /**
   * Brings every known table in line with the current viewport width.
   */
  function syncScrollableTables () {
    scrollableTables.forEach((table) => {
      const wrapped = isWrapped(table);

      if (MOBILE_QUERY.matches && !wrapped) {
        wrapTable(table);
      } else if (!MOBILE_QUERY.matches && wrapped) {
        unwrapTable(table);
      } else if (wrapped) {
        // Still mobile, but the available width changed — the table may now
        // fit, or no longer fit.
        updateScrollFades(table.parentNode.parentNode);
      }
    });
  }

  MOBILE_QUERY.addEventListener('change', syncScrollableTables);
  $window.on('resize', syncScrollableTables);

  // How many `p.book` / `p.article` rows stay visible before a run collapses
  // behind its "Read More" toggle (Figma 20472-13877).
  const ICON_PARAGRAPH_VISIBLE = 3;
  // Feeds the generated ids that the toggle's aria-controls points at.
  let iconParagraphUid = 0;

  /**
   * Splits icon paragraphs into runs of adjacent siblings of the same kind.
   *
   * A run ends as soon as anything else comes between two of them — another
   * paragraph, a heading, a different icon class — so a list of books followed
   * by a list of articles collapses as two independent groups.
   *
   * @param array els The icon paragraphs, in document order.
   *
   * @return array An array of runs, each an array of elements.
   */
  function groupIconParagraphRuns (els) {
    return els.reduce((runs, el) => {
      const current = runs[runs.length - 1];
      const previous = current && current[current.length - 1];
      const continues = previous
        && el.previousElementSibling === previous
        && el.className === previous.className;

      if (continues) {
        current.push(el);
      } else {
        runs.push([el]);
      }

      return runs;
    }, []);
  }

  /**
   * Moves everything past the third row of a run into a collapsible panel and
   * adds the "Read More" button that opens it.
   *
   * The rows move into `.icon-paragraph-more` rather than being hidden where
   * they stand, so the open/close can animate: the panel is a grid whose
   * single row goes from `0fr` to `1fr`, which resolves to the rows' own
   * height (see _global.scss) — no measuring here, and no max-height to keep
   * in step with the content. Runs of three or fewer are left alone.
   *
   * @param array items One run of adjacent icon paragraphs.
   */
  function collapseIconParagraphRun (items) {
    if (items.length <= ICON_PARAGRAPH_VISIBLE) {
      return;
    }

    const panel = document.createElement('div');
    const inner = document.createElement('div');
    const toggle = document.createElement('button');
    const label = document.createElement('span');

    iconParagraphUid += 1;
    panel.className = 'icon-paragraph-more';
    panel.id = `icon-paragraph-more-${iconParagraphUid}`;
    inner.className = 'icon-paragraph-more__inner';
    panel.appendChild(inner);

    const setExpanded = (expanded) => {
      toggle.setAttribute('aria-expanded', String(expanded));
      label.textContent = expanded ? Drupal.t('Read Less') : Drupal.t('Read More');
      panel.classList.toggle('is-open', expanded);
      // The closed panel is only clipped, not `display: none` — which is what
      // lets it animate — so `inert` is what keeps the rows inside it out of
      // the tab order and off screen readers.
      panel.inert = !expanded;
    };

    toggle.type = 'button';
    toggle.className = 'icon-paragraph-toggle';
    toggle.setAttribute('aria-controls', panel.id);
    toggle.appendChild(label);
    toggle.addEventListener('click', () => {
      setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
    });

    items[items.length - 1].after(panel);
    items.slice(ICON_PARAGRAPH_VISIBLE).forEach((item) => inner.appendChild(item));
    panel.after(toggle);
    setExpanded(false);
  }

  // Distance the page has to be scrolled before the back-to-top button shows.
  const BACK_TO_TOP_OFFSET = 100;
  // Applied while the control sits over the footer, which is dark enough that
  // the default light treatment stops reading — see _global.scss.
  const BACK_TO_TOP_DARK_CLASS = 'back-to-top--on-dark';
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  let backToTopButton = null;
  let backToTopFooter = null;
  let backToTopFrame = null;

  /**
   * Toggles the back-to-top button once the page is scrolled past the offset,
   * and inverts it while it overlaps the footer.
   */
  function updateBackToTop () {
    backToTopFrame = null;

    backToTopButton.classList.toggle('show', window.scrollY > BACK_TO_TOP_OFFSET);

    if (!backToTopFooter) {
      return;
    }

    // Compared as rectangles rather than as a scroll offset, so the test keeps
    // working with the button's responsive `bottom` values and with a footer
    // whose height changes (menu columns wrap on narrow viewports).
    const button = backToTopButton.getBoundingClientRect();
    const footer = backToTopFooter.getBoundingClientRect();
    const overlapsFooter = footer.top < button.bottom && footer.bottom > button.top;

    backToTopButton.classList.toggle(BACK_TO_TOP_DARK_CLASS, overlapsFooter);
  }

  /**
   * Collapses a burst of scroll events into one class update per frame — scroll
   * fires far more often than the browser paints, and the original handler ran
   * a jQuery read plus two class writes on every single event.
   */
  function queueBackToTopUpdate () {
    if (backToTopFrame === null) {
      backToTopFrame = window.requestAnimationFrame(updateBackToTop);
    }
  }

  /**
   * Returns to the top of the page, honouring `prefers-reduced-motion`.
   *
   * @param object event The click event on the back-to-top control.
   */
  function scrollToTop (event) {
    event.preventDefault();

    window.scrollTo({
      top: 0,
      behavior: REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth',
    });
  }

  /**
   * Main script for the current theme
   */
  Drupal.behaviors.themeJS = {
    /**
     * Trigger this function when the pages and Drupal js core was loaded
     *
     * @param object context Dom element triggered this method
     * @param object settings Curent Drupal settings
     *
     */
    attach (context/* , settings */) {
      this.positionHeaderSearchAutocomplete(context);

      /* Show and hide header on scrolling. */
      const SCROLL_THRESHOLD = 200;
      const CLASS_SCROLLED = 'js-did-scroll';

      const $body = $('body', context);

      $window.on('scroll', () => {
        const offsetTop = $window.scrollTop();
        const didPassThreshold = offsetTop > SCROLL_THRESHOLD;

        if (didPassThreshold && !$body.hasClass(CLASS_SCROLLED)) {
          $body.addClass(CLASS_SCROLLED);
        } else if (!didPassThreshold && $body.hasClass(CLASS_SCROLLED)) {
          $body.removeClass(CLASS_SCROLLED);
        }
      });

      this.wrapIconParagraphContent(context);
      this.wrapTables(context);
      this.initBackToTop(context);
    },

    /**
     * Wires up the "back to top" control.
     *
     * @param object context Dom element triggered this method
     */
    initBackToTop (context) {
      once('back-to-top', '#back-to-top', context).forEach((button) => {
        backToTopButton = button;
        backToTopFooter = document.querySelector('.sdc-footer');

        window.addEventListener('scroll', queueBackToTopUpdate, { passive: true });
        button.addEventListener('click', scrollToTop);

        // Sync straight away: the page may already be scrolled on load, e.g.
        // after a browser restore or a fragment link.
        updateBackToTop();
      });
    },

    /**
     * Right-aligns the search-api-autocomplete dropdown in the header so it
     * doesn't overflow the viewport when the input is near the right edge.
     */
    positionHeaderSearchAutocomplete (context) {
      const inputs = once(
        'header-search-autocomplete-position',
        '.sdc-header-search .ui-autocomplete-input[data-search-api-autocomplete-search]',
        context,
      );

      inputs.forEach((input) => {
        const uiAutocomplete = $(input).data('ui-autocomplete');
        if (!uiAutocomplete) {
          return;
        }
        uiAutocomplete.options.position = {
          my: 'right top+4',
          at: 'right bottom',
          collision: 'none',
        };
      });
    },

    /**
     * Wraps `.text-formatted p.book`/`p.article` content in a <span>, then
     * collapses long runs of them behind a "Read More" toggle.
     *
     * `<p class="book">Content</p>` becomes
     * `<p class="book"><span>Content</span></p>`, giving the text its own
     * element alongside the `::before` icon tile (see _global.scss) instead
     * of relying on the paragraph's anonymous flex-item text node.
     *
     * Adjacent rows of the same kind are then grouped, and any group longer
     * than three keeps its first three rows on screen with the rest behind a
     * toggle (Figma 20472-13877).
     */
    wrapIconParagraphContent (context) {
      const els = once(
        'text-formatted-icon-paragraph',
        '.text-formatted p.book, .text-formatted p.article',
        context,
      );

      els.forEach((el) => {
        const span = document.createElement('span');
        while (el.firstChild) {
          span.appendChild(el.firstChild);
        }
        el.appendChild(span);
      });

      groupIconParagraphRuns(els).forEach(collapseIconParagraphRun);
    },

    /**
     * Registers `.text-formatted` tables for the mobile scroll wrapper.
     *
     * The wrapping itself is width-dependent and happens in
     * syncScrollableTables() — tables found here are only remembered, so a
     * later resize can wrap or unwrap them.
     */
    wrapTables (context) {
      once('table-wrapper', '.text-formatted table:not(.tablesaw)', context)
        .forEach((table) => scrollableTables.add(table));

      syncScrollableTables();
    },
  };
}(jQuery, Drupal, window, document));
